<template>
	<form ref="contactform" method="post" class="contact-form" @submit.prevent="submit()">
		<div v-if="state.errors.length" class="errors">
			<div v-for="(error, idx) in state.errors" :key="idx" class="error">
				{{ $t(error) }}
			</div>
		</div>
		<div v-for="field in formRef" :key="field.name" :class="[field.name, field.edittype]">
			<div v-if="field.error" class="error">
				{{ $t(field.error) }}
			</div>
			<checkbox-group
				v-if="field.edittype === 'checkboxgroup'"
				v-model:checkboxgroup="field.value as string[]"
				:field="field"
			/>
			<date-field
				v-else-if="field.name === 'eventDate'"
				v-model:datefield="field.value as string"
				:field="field"
			/>
			<form-field v-else :field="field" />
		</div>
		<input type="submit" class="button button-cta action" :value="$t('formSubmit')" />
	</form>
</template>

<script setup lang="ts">
const props = defineProps<{
	form: FormField[];
	name: string;
}>();

const { submitForm, prepareSubmitData, setErrors, clearErrors, resetForm } = useForm();

const contactform = ref<HTMLFormElement>();
const formRef = ref<FormField[]>([...props.form]);
const state = ref<{ errors: string[] }>({ errors: [] });

const submit = async () => {
	clearErrors(state, formRef.value);

	const result = await submitForm(prepareSubmitData(formRef.value), props.name);
	if (result.status !== 'success') {
		setErrors(state, result, formRef.value);

		window.scroll({
			top: (contactform.value?.offsetTop ?? 0) - 150,
			left: 0,
			behavior: 'smooth',
		});

		return;
	}

	resetForm(formRef.value, contactform, state);
	await navigateTo(result.redirect);
};

const { $installRecaptcha } = useNuxtApp();
onMounted(() => {
	$installRecaptcha();
});
</script>

<style lang="scss" scoped>
form {
	margin: 0;
	display: flex;
	flex-flow: row wrap;
	justify-content: space-between;
	align-items: flex-start;

	> div {
		width: 49%;

		&.errors,
		&.textarea,
		&.consent,
		&.checkboxgroup {
			width: 100%;
		}
	}

	:deep(::placeholder) {
		color: #fff;
	}

	:deep(label) {
		color: #fff;
		margin: 0 0 5px;
		text-decoration: none;
		display: inline-block;
		width: 100%;
	}

	:deep(.error) {
		display: block;
		margin: 0 0 10px;
	}

	:deep(input:not([type='checkbox'], [type='radio'], [type='submit'])),
	:deep(select),
	:deep(textarea) {
		color: #333;
		width: 100%;
		padding: 15px;
		border: none;
		border-radius: 0;
		margin: 0 0 25px;
		font-size: 13px;
		font-family: var(--body-font-family);
		height: 34px;
	}

	:deep(input:not([disabled], [type='submit'])),
	:deep(select),
	:deep(textarea) {
		background: #fff;
	}

	:deep(select) {
		background: url('~/assets/images/icon-form-dropdown.png') no-repeat var(--primary-background-color);
		background-position: right 15px top 50%;
		appearance: none;
	}

	input[type='submit'] {
		width: auto;
	}

	:deep(textarea) {
		min-height: 140px;
		max-width: 100%;
	}

	.button {
		outline: none;
		border: none;
		color: var(--primary-background-color);
		background: #fff;
		text-transform: uppercase;
		font-family: var(--heading-font-family);
		padding: 3px 9px 5px;
		cursor: pointer;
		font-size: 16px;
		letter-spacing: 2px;
		margin: 0 0 25px;
		transition: all 0.3s ease-in-out;

		&:hover {
			background: #fdf0f4;
		}
	}
}

@media (max-width: 1280px) {
	form {
		> div {
			width: 100%;
		}
	}
}
</style>
